import * as Sentry from '@sentry/nextjs';

const PII_KEY_PATTERN =
  /plate|vin|code|email|phone|name|address|passport|personal|policyholder|license/i;

function sanitizeValue(value, depth = 0) {
  if (depth > 3 || value == null) return value;
  if (typeof value === 'string') {
    return value.length > 200 ? `${value.slice(0, 200)}…` : value;
  }
  if (Array.isArray(value)) {
    return value.slice(0, 10).map(item => sanitizeValue(item, depth + 1));
  }
  if (typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value)
        .filter(([key]) => !PII_KEY_PATTERN.test(key))
        .map(([key, val]) => [key, sanitizeValue(val, depth + 1)])
    );
  }
  return value;
}

/** Keep calculator context on the active Sentry scope (tags + context). */
export function setCalculatorSentryContext({
  insuranceType,
  insuranceSlug,
  step,
} = {}) {
  Sentry.setTag('feature', 'skaiciuokle');
  if (insuranceType) Sentry.setTag('insurance_type', insuranceType);
  if (insuranceSlug) Sentry.setTag('insurance_slug', insuranceSlug);
  if (step !== undefined && step !== null) {
    Sentry.setTag('calculator_step', String(step));
  }
  Sentry.setContext(
    'calculator',
    sanitizeValue({
      insurance_type: insuranceType,
      insurance_slug: insuranceSlug,
      step,
    })
  );
}

function isUserValidationError(error) {
  return Boolean(
    error?.fieldErrors && Object.keys(error.fieldErrors).length > 0
  );
}

/**
 * Report a calculator flow error to Sentry with skaiciuokle tags.
 * Skips aborted requests and expected form validation failures.
 */
export function captureCalculatorError(
  error,
  { operation, endpoint, component, extra, level = 'error' } = {}
) {
  if (!error || error.name === 'AbortError') return;
  if (isUserValidationError(error)) return;

  Sentry.withScope(scope => {
    scope.setTag('feature', 'skaiciuokle');
    if (operation) scope.setTag('api_operation', operation);
    if (endpoint) scope.setTag('api_endpoint', endpoint);
    if (component) scope.setTag('component', component);
    scope.setLevel(level);

    const context = sanitizeValue({
      message: error.message,
      ...extra,
    });
    if (Object.keys(context).length > 0) {
      scope.setContext('calculator_error', context);
    }

    Sentry.captureException(error);
  });
}

/** Shorthand for calculator API failures in src/utils/api.js */
export function captureCalculatorApiError(error, operation, endpoint, extra) {
  captureCalculatorError(error, { operation, endpoint, extra });
}
